/* eslint-disable no-console */

type SyncFields = Record<string, string | number | boolean | undefined>;

let isEnabled = false;
let realmId: string | undefined;
let sequence = 0;

export function isSyncDebugEnabled(): boolean {
  return isEnabled;
}

export function logSync(event: string, fields?: SyncFields) {
  if (!isEnabled) return;

  try {
    // This identifier is local to this realm, unrelated to accounts, sessions, or tab tokens.
    realmId ||= `${typeof document === 'undefined' ? 'worker' : 'main'}-${Math.random().toString(36).slice(2, 14)}`;
    const boundedFields: SyncFields = {};
    if (fields) {
      Object.keys(fields).slice(0, 32).forEach((key) => {
        if (key.length > 64 || key === '__proto__') return;
        const value = fields[key];
        if (typeof value === 'string') {
          boundedFields[key] = value.slice(0, 256);
        } else if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
          boundedFields[key] = value;
        }
      });
    }
    sequence += 1;
    console.info('[SyncDebug]', {
      timestamp: new Date().toISOString(),
      realmId,
      sequence,
      event: event.slice(0, 96),
      fields: boundedFields,
    });
  } catch (err) {
    // Diagnostics must not interrupt sync, including when console forwarding fails.
  }
}

function logEnvironment() {
  logSync('environment', { visibility: document.visibilityState, online: navigator.onLine });
}

function logVisibility() {
  logSync('visibility', { visibility: document.visibilityState });
}

function logNetwork(event: Event) {
  logSync(event.type, { online: navigator.onLine });
}

export function setSyncDebugEnabled(enabled: boolean) {
  if (isEnabled === enabled) return;
  isEnabled = enabled;

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (enabled) {
    document.addEventListener('visibilitychange', logVisibility);
    window.addEventListener('online', logNetwork);
    window.addEventListener('offline', logNetwork);
    logEnvironment();
  } else {
    document.removeEventListener('visibilitychange', logVisibility);
    window.removeEventListener('online', logNetwork);
    window.removeEventListener('offline', logNetwork);
  }
}

type SyncOrigin = { origin: 'push' | 'rpc' | 'difference'; requestId?: number };

const syncOrigins = new WeakMap<object, SyncOrigin>();

export function setSyncOrigin(object: object, origin: SyncOrigin['origin'], requestId?: number) {
  if (!isEnabled) return;
  syncOrigins.set(object, { origin, requestId });
}

export function getSyncOrigin(object: object): Readonly<SyncOrigin> | undefined {
  return isEnabled ? syncOrigins.get(object) : undefined;
}
